import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

type InviteInfo = { valid: boolean; companyName?: string; email?: string };

export default function AcceptInvitePage() {
  const [params]    = useSearchParams();
  const navigate    = useNavigate();
  const { user } = useAuth();
  const token = params.get('token') ?? '';

  const [info, setInfo]       = useState<InviteInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError]     = useState('');
  const [done, setDone]       = useState(false);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    api.get<InviteInfo>(`/companies/invite-info?token=${token}`)
      .then(r => { setInfo(r); setLoading(false); })
      .catch(() => { setInfo({ valid: false }); setLoading(false); });
  }, [token]);

  async function accept() {
    setAccepting(true); setError('');
    try {
      await api.post('/companies/accept-invite', { token });
      setDone(true);
      setTimeout(() => navigate('/settings?tab=company'), 2000);
    } catch (err: any) {
      setError(err.message ?? 'Could not accept the invitation.');
    } finally { setAccepting(false); }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#f4f4f4' }}>
        <p style={{ color: '#8f9b9b' }}>Checking invitation…</p>
      </div>
    );
  }

  if (!token || !info?.valid) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: '#f4f4f4' }}>
        <div className="w-full max-w-sm text-center">
          <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: '#fee2e2' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#e00000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
          </div>
          <h2 className="text-xl font-bold mb-2" style={{ color: '#0b0d0e' }}>Invitation expired</h2>
          <p className="text-sm mb-6" style={{ color: '#8f9b9b' }}>This invite link is no longer valid. It may have expired or already been used. Ask your company owner to send a new invitation.</p>
          <Link to="/dashboard" className="btn-primary">Go to dashboard</Link>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: '#f4f4f4' }}>
        <div className="w-full max-w-sm text-center">
          <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: '#dcfce7' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
          <h2 className="text-xl font-bold mb-2" style={{ color: '#0b0d0e' }}>You're in!</h2>
          <p className="text-sm" style={{ color: '#8f9b9b' }}>You've joined <strong>{info.companyName}</strong>. Redirecting to settings…</p>
        </div>
      </div>
    );
  }

  // Email mismatch check
  const emailMismatch = user && info.email && user.email.toLowerCase() !== info.email.toLowerCase();

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: '#f4f4f4' }}>
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="https://cdn.shopify.com/s/files/1/0774/8055/8750/files/MontiPower_Same_Font_Copyright_Original_Black_Red_1200px.png?v=1787509899"
            alt="MONTIPOWER" style={{ height: 24, width: 'auto', margin: '0 auto 6px' }}
          />
          <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#aaa' }}>Warranty Portal</p>
        </div>

        <div className="card text-center">
          <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: '#f4f4f4' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#e00000" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 21h18M3 7l9-4 9 4M4 7v14M20 7v14M9 21V11h6v10"/>
            </svg>
          </div>
          <h2 className="text-xl font-bold mb-1" style={{ color: '#0b0d0e' }}>You're invited</h2>
          <p className="text-sm mb-1" style={{ color: '#8f9b9b' }}>You've been invited to join</p>
          <p className="text-lg font-bold mb-4" style={{ color: '#0b0d0e' }}>{info.companyName}</p>
          {info.email && (
            <p className="text-xs mb-6 py-2 px-3 rounded" style={{ background: '#f4f4f4', color: '#8f9b9b' }}>
              This invite was sent to <strong style={{ color: '#0b0d0e' }}>{info.email}</strong>
            </p>
          )}

          {error && <div className="alert-error mb-4 text-left">{error}</div>}

          {emailMismatch ? (
            <div className="mb-4 p-3 rounded-lg text-sm text-left" style={{ background: '#fff7ed', border: '1px solid #fed7aa', color: '#c2410c' }}>
              <p className="font-semibold mb-1">Wrong account</p>
              <p style={{ fontSize: 13 }}>This invite was sent to <strong>{info.email}</strong>, but you're signed in as <strong>{user?.email}</strong>. Please sign out and sign in with the correct account.</p>
            </div>
          ) : !user ? (
            <div className="mb-4 p-3 rounded-lg text-sm" style={{ background: '#f4f4f4', color: '#0b0d0e' }}>
              <p className="mb-2">You need to be signed in to accept this invitation.</p>
              <div className="flex gap-2 justify-center">
                <Link to={`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`} className="btn-primary text-sm">Sign in</Link>
                <Link to={`/register?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`}
                  className="text-sm px-4 py-2 rounded font-semibold" style={{ border: '1px solid #d9d9d9', color: '#0b0d0e' }}>Register</Link>
              </div>
            </div>
          ) : (
            <button className="btn-primary w-full" onClick={accept} disabled={accepting}>
              {accepting ? 'Joining…' : `Join ${info.companyName}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
